/**
 * Zod Error Map with i18next Integration
 *
 * Translates Zod validation errors using i18next translation keys.
 * Compatible with Zod v4 (issue codes and field names changed from v3).
 *
 * Zod v4 changes vs v3:
 *   - 'invalid_string' → 'invalid_format'
 *   - issue.validation → issue.format (for string format checks)
 *   - issue.type → issue.origin (for too_small / too_big)
 *   - Error map return type allows undefined/null (falls through to next map)
 */

import { z } from 'zod';
import i18n from './config';

export const makeZodI18nMap = (): z.ZodErrorMap => (issue: any) => {
  let message: string | undefined;

  const code: string | undefined = issue.code;

  switch (code) {
    case 'invalid_type':
      if (issue.received === 'undefined' || issue.received === 'null' || issue.received == null) {
        message = i18n.t('validation:required');
      } else {
        message = i18n.t('validation:invalid_type');
      }
      break;

    // Zod v4 uses 'invalid_format'; v3 used 'invalid_string' — handle both
    case 'invalid_format':
    case 'invalid_string': {
      const fmt: string | undefined = issue.format ?? issue.validation;
      if (fmt === 'email') {
        message = i18n.t('validation:invalid_email');
      } else if (fmt === 'url' || fmt === 'uri') {
        message = i18n.t('validation:invalid_url');
      } else if (fmt === 'uuid') {
        message = i18n.t('validation:invalid_uuid');
      } else if (fmt === 'datetime') {
        message = i18n.t('validation:invalid_datetime');
      } else {
        message = i18n.t('validation:invalid_string');
      }
      break;
    }

    case 'too_small': {
      // Zod v4 uses issue.origin; v3 used issue.type
      const origin: string | undefined = issue.origin ?? issue.type;
      if (origin === 'string') {
        const key = issue.exact
          ? 'validation:too_small.string.exact'
          : issue.inclusive
          ? 'validation:too_small.string.inclusive'
          : 'validation:too_small.string.not_inclusive';
        message = i18n.t(key, { minimum: issue.minimum });
      } else if (origin === 'number' || origin === 'int') {
        const key = issue.exact
          ? 'validation:too_small.number.exact'
          : issue.inclusive
          ? 'validation:too_small.number.inclusive'
          : 'validation:too_small.number.not_inclusive';
        message = i18n.t(key, { minimum: issue.minimum });
      } else if (origin === 'array') {
        const key = issue.exact
          ? 'validation:too_small.array.exact'
          : issue.inclusive
          ? 'validation:too_small.array.inclusive'
          : 'validation:too_small.array.not_inclusive';
        message = i18n.t(key, { minimum: issue.minimum });
      }
      break;
    }

    case 'too_big': {
      const origin: string | undefined = issue.origin ?? issue.type;
      if (origin === 'string') {
        const key = issue.exact
          ? 'validation:too_big.string.exact'
          : issue.inclusive
          ? 'validation:too_big.string.inclusive'
          : 'validation:too_big.string.not_inclusive';
        message = i18n.t(key, { maximum: issue.maximum });
      } else if (origin === 'number' || origin === 'int') {
        const key = issue.exact
          ? 'validation:too_big.number.exact'
          : issue.inclusive
          ? 'validation:too_big.number.inclusive'
          : 'validation:too_big.number.not_inclusive';
        message = i18n.t(key, { maximum: issue.maximum });
      } else if (origin === 'array') {
        const key = issue.exact
          ? 'validation:too_big.array.exact'
          : issue.inclusive
          ? 'validation:too_big.array.inclusive'
          : 'validation:too_big.array.not_inclusive';
        message = i18n.t(key, { maximum: issue.maximum });
      }
      break;
    }

    case 'invalid_date':
      message = i18n.t('validation:invalid_date');
      break;

    default:
      // Return undefined to let Zod's built-in locale error map supply the message
      return undefined;
  }

  return message !== undefined ? { message } : undefined;
};

/**
 * Set the global Zod error map to use i18next translations.
 * Call once during app initialization.
 */
export const setZodErrorMap = () => {
  z.setErrorMap(makeZodI18nMap());
};
